import React, { useEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'

// Layout Components
import RootLayout from '@components/layout/RootLayout'

// A/B Testing and Performance Monitoring
import { initializeMonitoring } from './utils/monitoringInit'

// Page Components
import HomePage from '@pages/HomePage'
import SearchPage from '@pages/SearchPage'
import CompanyDetailPage from '@pages/CompanyDetailPage'
import AboutPage from '@pages/AboutPage'
import ContactPage from '@pages/ContactPage'
import FAQPage from '@pages/FAQPage'
import PrivacyPage from '@pages/PrivacyPage'
import TermsPage from '@pages/TermsPage'
import HelpPage from '@pages/HelpPage'
import ReportPage from '@pages/ReportPage'
import FeedbackPage from '@pages/FeedbackPage'
import SitemapPage from '@pages/SitemapPage'
import NotFoundPage from '@pages/NotFoundPage'
import RegisterPage from '@pages/RegisterPage'
import LoginPage from '@pages/LoginPage'
import ProfilePage from '@pages/ProfilePage'
import MyClaimsPage from '@pages/MyClaimsPage'
import MyCompaniesPage from '@pages/MyCompaniesPage'
import MyRequestsPage from '@pages/MyRequestsPage'
import CreateCompanyPage from '@pages/CreateCompanyPage'
import SettingsPage from '@pages/SettingsPage'
import NotificationsPage from '@pages/NotificationsPage'

// Admin Components
import AdminRoutes from './routes/adminRoutes'
import { AdminAuthProvider } from './hooks/admin'

// SEO Component
import SEO from '@components/common/SEO'

// Types
import type { FC } from 'react'

const App: FC = () => {
  const location = useLocation()

  // Initialize monitoring systems
  useEffect(() => {
    initializeMonitoring()
  }, [])

  return (
    <>
      {/* Global SEO */}
      <SEO />

      <Routes>
        {/* Admin Routes */}
        <Route
          path="/admin/*"
          element={
            <AdminAuthProvider>
              <AdminRoutes />
            </AdminAuthProvider>
          }
        />

        {/* Public Routes with RootLayout (including Auth pages) */}
        <Route
          path="/*"
          element={
            <RootLayout>
              <AnimatePresence mode="wait" initial={false}>
                <Routes location={location} key={location.pathname}>
                  {/* Home Page */}
                  <Route
                    path="/"
                    element={<HomePage />}
                  />

                  {/* Auth Routes (now with header) */}
                  <Route
                    path="/register"
                    element={<RegisterPage />}
                  />

                  <Route
                    path="/login"
                    element={<LoginPage />}
                  />

                  {/* Profile Page (Protected) */}
                  <Route
                    path="/profile"
                    element={<ProfilePage />}
                  />

                  {/* My Claims Page (Protected) */}
                  <Route
                    path="/my-claims"
                    element={<MyClaimsPage />}
                  />

                  {/* My Companies Page (Protected) */}
                  <Route
                    path="/my-companies"
                    element={<MyCompaniesPage />}
                  />

                  {/* My Requests Page (Protected) */}
                  <Route
                    path="/my-requests"
                    element={<MyRequestsPage />}
                  />

                  {/* Create Company Page (Protected) */}
                  <Route
                    path="/create-company"
                    element={<CreateCompanyPage />}
                  />

                  {/* Settings Page (Protected) */}
                  <Route
                    path="/settings"
                    element={<SettingsPage />}
                  />

                  {/* Notifications Page (Protected) */}
                  <Route
                    path="/notifications"
                    element={<NotificationsPage />}
                  />

                  {/* Search Results */}
                  <Route
                    path="/search"
                    element={<SearchPage />}
                  />

                  {/* Company Detail */}
                  <Route
                    path="/companies/:slug"
                    element={<CompanyDetailPage />}
                  />

                  {/* Static Pages */}
                  <Route
                    path="/about"
                    element={<AboutPage />}
                  />

                  <Route
                    path="/contact"
                    element={<ContactPage />}
                  />

                  <Route
                    path="/faq"
                    element={<FAQPage />}
                  />

                  {/* Privacy & Terms */}
                  <Route
                    path="/privacy"
                    element={<PrivacyPage />}
                  />

                  <Route
                    path="/terms"
                    element={<TermsPage />}
                  />

                  {/* Help & Support */}
                  <Route
                    path="/help"
                    element={<HelpPage />}
                  />

                  <Route
                    path="/report"
                    element={<ReportPage />}
                  />

                  <Route
                    path="/feedback"
                    element={<FeedbackPage />}
                  />

                  {/* Sitemap */}
                  <Route
                    path="/sitemap"
                    element={<SitemapPage />}
                  />

                  {/* 404 Not Found */}
                  <Route
                    path="*"
                    element={<NotFoundPage />}
                  />
                </Routes>
              </AnimatePresence>
            </RootLayout>
          }
        />
      </Routes>
    </>
  )
}

export default App